<script setup lang="ts">
import { SfIconChevronLeft, SfIconChevronRight } from '@storefront-ui/vue';
import { useOffsetPagination } from '@vueuse/core';

const props = defineProps<{
  current_page: number;
  total: number;
  per_page: number;
  pages_count: number;
}>();

const { page } = useSearch();

// Make props reactive so they update correctly
const totalItems = computed(() => props.total);
const pageSize = computed(() => props.per_page);

// Pagination logic using useOffsetPagination
const {
  currentPage,
  pageCount,
  isFirstPage,
  isLastPage, 
} = useOffsetPagination({
  total: totalItems,
  page: toRef(props, 'current_page'), // Bind to props reactively
  pageSize,
  onPageChange: () => { 
    window.scrollTo({ top: 10, behavior: 'smooth' });
    page.value = currentPage.value;
  }
});

// Generate pagination numbers with ellipses according to the desired patterns
const pages = computed(() => {
  const totalPages = pageCount.value;
  const current = currentPage.value;
  const range: (number | string)[] = [];

  if (totalPages <= 6) {
    // If there are 6 or fewer pages, display them all.
    for (let i = 1; i <= totalPages; i++) {
      range.push(i);
    }
  } else {
    // Beginning section
    if (current <= 2) {
      // For page 1 and 2: show pages 1,2,3 then ellipsis then last
      range.push(1, 2, 3);
      range.push('...');
      range.push(totalPages);
    } else if (current === 3) {
      // For page 3: show pages 1,2,3,4 then ellipsis then last
      range.push(1, 2, 3, 4);
      range.push('...');
      range.push(totalPages);
    }
    // Ending section
    else if (current >= totalPages - 1) {
      // For the last two pages: show first page, ellipsis, then the last three pages
      range.push(1);
      range.push('...');
      range.push(totalPages - 2, totalPages - 1, totalPages);
    } else if (current === totalPages - 2) {
      // For page total-2 (e.g. page 8 of 10): show first page, ellipsis, then last four pages
      range.push(1);
      range.push('...');
      range.push(totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
    }
    // Middle section
    else {
      // For middle pages: show first, ellipsis, current-1, current, current+1, ellipsis, last
      range.push(1);
      range.push('...');
      range.push(current - 1, current, current + 1);
      range.push('...');
      range.push(totalPages);
    }
  }

  return range;
});

// Change page function
function goToPage(p: number) {
  if (p !== currentPage.value) {
    currentPage.value = p;
    page.value = p;
  }
}
</script>

<template>
  <nav
    class="flex border-t border-neutral-200 justify-between items-center md:justify-start pt-3"
    role="navigation"
    aria-label="pagination"
    data-testid="pagination"
  >
    <button
      class="px-4 py-3 btn flex whitespace-nowrap rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
      @click="goToPage(currentPage - 1)"
      :disabled="isFirstPage"
    >
      <SfIconChevronLeft /> {{ $t('Prev') }}
    </button>
    <ul class="justify-center hidden md:flex items-center gap-1">
      <li v-for="(pageNum, index) in pages" :key="index">
        <button
          v-if="pageNum !== '...'"
          type="button"
          :class="{ 'text-primary-500 bg-primary-100': pageNum === currentPage }"
          class="btn md:w-12 rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
          @click="goToPage(pageNum as number)"
        >
          {{ pageNum }}
        </button>
        <span
          v-else
          class="px-2 text-neutral-400 text-sm"
        >
          ...
        </span>
      </li>
    </ul>
    <button
      class="px-4 py-3 btn flex whitespace-nowrap rounded-md text-neutral-500 hover:bg-primary-100 hover:text-primary-800 active:bg-primary-200 active:text-primary-900"
      @click="goToPage(currentPage + 1)"
      :disabled="isLastPage"
    >
      {{ $t('Next') }} <SfIconChevronRight />
    </button>
  </nav>
</template>
